/**
 * The single decision point for "is this result refinable, by whom, and what
 * should we ask for next".
 *
 * Every predicate here is lifted verbatim from the shipped UI so the server
 * answer and the client fallback cannot drift:
 *  - identify abstention reasons: components/home/results-display.tsx
 *    (`canRefineWithSupportingPhoto`, `isUnknownOther`, `isFailedAnalysis`)
 *  - identify low margin: isLowConfidence() in lib/confidence-display.ts
 *  - diagnose: the quality envelope's `status === 'abstained'`
 *  - weed: canAccumulateWeedPhotos() in lib/analysis/weed-photo-accumulation.ts
 *
 * Entitlement (how many photos, how many free re-runs) comes from
 * lib/analysis/photo-caps.ts plus the rescan-exemption caps applied in the
 * route. This module never talks to the database.
 */

import type { AnalysisMode } from '@/lib/types/diagnosis'
import { isLowConfidence } from '@/lib/confidence-display'
import { canAccumulateWeedPhotos, weedRefinementGuidance } from '@/lib/analysis/weed-photo-accumulation'
import { MODE_PHOTO_CEILING, resolvePhotoCap, type PhotoTier } from '@/lib/analysis/photo-caps'

export type RefinementReason =
  /** abstained: insufficient_visual_evidence */
  | 'inconclusive'
  /** abstained: poor_image */
  | 'poor_image'
  /** a real primary grass, but below the low-confidence cutoff */
  | 'low_margin'
  /** Unknown/Other rendering the alternatives picker */
  | 'picker_fallback'
  /** weed abstention a further view can actually move */
  | 'weed_accumulation'
  /**
   * A replacement rescan of the same root returned a DIFFERENT identity.
   * Confidence is irrelevant here: two confident answers that disagree are the
   * strongest possible reason to read both photos together.
   */
  | 'conflicting_rescan'

export const REFINEMENT_REASONS: readonly RefinementReason[] = [
  'inconclusive',
  'poor_image',
  'low_margin',
  'picker_fallback',
  'weed_accumulation',
  'conflicting_rescan',
]

const REFINEMENT_REASON_SET = new Set<string>(REFINEMENT_REASONS)

/**
 * Client-supplied analytics label. Never trusted for quota — the server derives
 * additive-vs-replacement itself. `photo_cap` is the gate fired when a request
 * was rejected for sending more photos than the tier allows. `retry_additive`
 * is the relabeled default retry on a settled result.
 */
export type RefinementTrigger = RefinementReason | 'photo_cap' | 'retry_additive'

const EXTRA_REFINEMENT_TRIGGERS = new Set<string>(['photo_cap', 'retry_additive'])

export function isRefinementTrigger(value: unknown): value is RefinementTrigger {
  return typeof value === 'string' &&
    (EXTRA_REFINEMENT_TRIGGERS.has(value) || REFINEMENT_REASON_SET.has(value))
}

/**
 * Per-mode fallback ask when the model named no specific next view. Identify
 * and diagnose copies previously lived inline in hooks/use-homepage-analysis.ts;
 * weed's lives in weedRefinementGuidance().
 */
export const REFINEMENT_GUIDANCE_FALLBACK: Record<AnalysisMode, string> = {
  identify: 'Add a bright, in-focus close-up that shows blade width, tips, and any runners or seedheads.',
  diagnose: 'Add a bright close-up of the damaged blades, or the edge where healthy grass meets the patch.',
  weed: 'Add a close-up of the leaf, stem, and any flower or seed head.',
}

/** One hop of a rescan chain, oldest first. Root is index 0. */
export interface RefinementChainEntry {
  analysisId: string
  /**
   * 1-based ordinal within the fetched window, assigned before unnamed hops
   * are dropped, so a hop that returned no identity cannot renumber the ones
   * around it. Render this, never the array index.
   */
  position: number
  /** identify: grassType. diagnose: primary problem name. weed: weedName. */
  label: string
  /** displayConfidence, else confidence, else null. Already rounded 0-100. */
  confidence: number | null
}

export interface RefinementPolicy {
  available: boolean
  reason: RefinementReason | null
  nextPhotoGuidance: string | null
  imageCount: number
  photoCap: number
  slotsRemaining: number
  /** slotsRemaining === 0 solely because the caller is anonymous. */
  requiresAuth: boolean
  /** slotsRemaining === 0 solely because the caller is registered free. */
  requiresUpgrade: boolean
  /** null = unlimited (premium). 0 = none left / not entitled. */
  refinementsRemaining: number | null
  /**
   * Free "Start over" replacement re-runs of this root still available to the
   * caller. Merged by the route, which owns the database read. null = unlimited
   * (premium/allowlisted); absent whenever nothing counted them, in which case
   * the server cap remains the only backstop and clients keep the offer up.
   */
  replacementRescansRemaining?: number | null
  /**
   * Merged by the route, which owns the database read; this module stays
   * DB-free. Present only on rescan responses with 2+ hops.
   */
  chain?: RefinementChainEntry[]
}

/**
 * Free additive re-runs per analysis root, by tier. Premium is unlimited;
 * anonymous gets none for identify/diagnose because the photo cap of 1 rejects
 * a multi-photo request before it can ever be additive.
 *
 * WEED is the deliberate exception, and it is tier-FLAT: its accumulation loop
 * ships free at up to 3 photos (lib/analysis/photo-caps.ts keeps the same flat
 * ceiling), so an account must never be the smaller allowance. Signing in used
 * to cut weed refinements from 3 to 1, which the client renders as a paywall at
 * photo 2 of 3 for exactly the users who just signed up.
 */
export const FREE_ADDITIVE_RESCANS_PER_ROOT = 1
export const WEED_ADDITIVE_RESCANS_PER_ROOT = 3

export function additiveRescanCapForTier(mode: AnalysisMode, tier: PhotoTier): number {
  if (tier === 'premium') return Number.POSITIVE_INFINITY
  if (mode === 'weed') return WEED_ADDITIVE_RESCANS_PER_ROOT
  if (tier === 'anonymous') return 0
  return FREE_ADDITIVE_RESCANS_PER_ROOT
}

type AnyResult = Record<string, any> | null | undefined

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.replace(/\s+/g, ' ').trim()
  return trimmed ? trimmed.slice(0, 240) : null
}

/** Mirrors `isFailedAnalysis` in components/home/results-display.tsx. */
function isFailedIdentify(result: AnyResult): boolean {
  if (!result) return true
  const grassType = typeof result.grassType === 'string' ? result.grassType : ''
  const isRecaptureSubject = result.subjectGuess === 'not_plant' || result.subjectGuess === 'unreadable'
  const isStructuredAbstention = grassType === 'Unknown/Other' && result.identificationStatus === 'abstained'
  return isRecaptureSubject ||
    (!isStructuredAbstention && result.confidence === 0) ||
    grassType.includes('Issue') ||
    grassType.includes('Unclear') ||
    grassType.includes('Unavailable') ||
    grassType.includes('Not a Grass Image')
}

/**
 * Abstentions another photo of the same subject cannot move: the plant is not
 * a turfgrass we support, so a re-run structurally cannot yield an ID and the
 * user would spend their free refinement on a guaranteed repeat. Mirrors
 * `deferUnknownPicker` in components/home/results-display.tsx, which already
 * routes these to the weed/diagnose reroute instead of the picker.
 */
const UNREFINABLE_ABSTENTION_REASONS = new Set(['unsupported_turfgrass', 'non_grass_subject'])
const UNREFINABLE_SUBJECT_GUESSES = new Set(['weed', 'other_plant'])

function identifyReason(result: AnyResult): RefinementReason | null {
  if (!result) return null
  const abstentionReason = typeof result.abstentionReason === 'string' ? result.abstentionReason : null
  if (abstentionReason === 'poor_image') return 'poor_image'
  if (abstentionReason === 'insufficient_visual_evidence') return 'inconclusive'
  if (abstentionReason && UNREFINABLE_ABSTENTION_REASONS.has(abstentionReason)) return null
  if (typeof result.subjectGuess === 'string' && UNREFINABLE_SUBJECT_GUESSES.has(result.subjectGuess)) {
    return null
  }

  const failed = isFailedIdentify(result)
  if (result.grassType === 'Unknown/Other' && !failed) return 'picker_fallback'
  if (failed) return null

  const effectiveConfidence = typeof result.displayConfidence === 'number'
    ? result.displayConfidence
    : typeof result.confidence === 'number' ? result.confidence : 100
  const userSelected = result.identitySource === 'user_selected'
  // Everything below the 70 confirmation threshold is presented as "closest
  // match", not a confirmed ID, so a confirm-it photo offer is honest there.
  // 60d data: <50 is ~2% of identifies, 50-69 another ~11% — this line is what
  // widened eligibility from failures-only to every unconfirmed result.
  if (!userSelected && (isLowConfidence(effectiveConfidence) || effectiveConfidence < 70)) {
    return 'low_margin'
  }
  return null
}

function diagnoseReason(result: AnyResult): RefinementReason | null {
  if (!result) return null
  const status = result?.quality?.status
  const abstained = status === 'abstained' || result.outcome === 'inconclusive'
  if (!abstained) return null
  const abstentionReason = result?.quality?.abstentionReason ?? result?.abstentionReason
  return abstentionReason === 'poor_image' ? 'poor_image' : 'inconclusive'
}

function weedReason(result: AnyResult): RefinementReason | null {
  return canAccumulateWeedPhotos(result as any) ? 'weed_accumulation' : null
}

export function resolveRefinementReason(mode: AnalysisMode, result: AnyResult): RefinementReason | null {
  if (mode === 'weed') return weedReason(result)
  if (mode === 'diagnose') return diagnoseReason(result)
  return identifyReason(result)
}

function normalizeIdentity(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const normalized = value.replace(/\s+/g, ' ').trim().toLowerCase()
  return normalized ? normalized : null
}

const WEED_IDENTIFIED_OUTCOMES = new Set(['supported', 'known'])

/**
 * Canonical comparable identity, or null when the result names no identity
 * (abstention, Unknown/Other, healthy lawn, failure). A null on either side
 * means "no conflict": a disagreement with an abstention is not a conflict.
 */
export function resolveIdentityKey(mode: AnalysisMode, result: unknown): string | null {
  const r = result as AnyResult
  if (!r) return null

  if (mode === 'weed') {
    if (!normalizeIdentity(r.weedName)) return null
    if (!WEED_IDENTIFIED_OUTCOMES.has(String(r.outcome))) return null
    // bestGuessName is presentation-only and never an identity.
    return normalizeIdentity(r.weedId) ?? normalizeIdentity(r.guideSlug) ?? normalizeIdentity(r.weedName)
  }

  if (mode === 'diagnose') {
    if (r.outcome === 'healthy' || r.outcome === 'inconclusive') return null
    const primary = Array.isArray(r.problems) ? r.problems[0] : null
    if (!primary) return null
    return normalizeIdentity(primary.conditionId) ?? normalizeIdentity(primary.name)
  }

  if (r.identitySource === 'user_selected') return null
  if (isFailedIdentify(r)) return null
  const grassType = normalizeIdentity(r.grassType)
  if (!grassType || grassType === 'unknown/other') return null
  return grassType
}

/** True only when both sides name an identity and the two disagree. */
export function hasConflictingIdentity(
  mode: AnalysisMode,
  priorResult: unknown,
  newResult: unknown,
): boolean {
  const prior = resolveIdentityKey(mode, priorResult)
  if (!prior) return false
  const next = resolveIdentityKey(mode, newResult)
  if (!next) return false
  return prior !== next
}

/**
 * The view to ask for next: the model's own request, then its first named
 * missing cue, then the per-mode fallback.
 */
/**
 * The conflict ask is a tiebreaker, not a re-read of both earlier photos: a
 * replacement rescan left the first photo behind, so the run that follows is
 * the photo on screen plus one new view. The copy names that mechanic.
 */
export const REFINEMENT_GUIDANCE_CONFLICT: Record<AnalysisMode, string> = {
  identify: 'Add one more view so we can settle which answer holds.',
  diagnose: 'Add one more view of the damage so we can settle which diagnosis holds.',
  weed: 'Add one more view so we can settle which answer holds.',
}

export function resolveNextPhotoGuidance(
  mode: AnalysisMode,
  result: AnyResult,
  reason?: RefinementReason | null,
): string {
  const conflicting = reason === 'conflicting_rescan'
  if (mode === 'weed' && !conflicting) return weedRefinementGuidance(result as any)
  const recommended = text(result?.recommendedNextPhoto) ?? text(result?.quality?.recommendedNextPhoto)
  if (recommended) return recommended
  if (conflicting) {
    return REFINEMENT_GUIDANCE_CONFLICT[mode] ?? REFINEMENT_GUIDANCE_CONFLICT.identify
  }
  const missingSource = Array.isArray(result?.missingEvidence)
    ? result?.missingEvidence
    : Array.isArray(result?.quality?.missingEvidence)
      ? result?.quality?.missingEvidence
      : []
  const missing = missingSource.map(text).find((item: string | null) => !!item)
  if (missing) return `Add a close-up that shows ${missing}.`
  return REFINEMENT_GUIDANCE_FALLBACK[mode] ?? REFINEMENT_GUIDANCE_FALLBACK.identify
}

export function resolveRefinementPolicy(input: {
  mode: AnalysisMode
  tier: PhotoTier
  result: unknown
  imageCount: number
  allowlisted?: boolean
  priorAdditiveRescans?: number
  /** The ROOT row's analysis_result jsonb. The caller does the database read. */
  priorChainResult?: unknown
  /** Server-derived classification of THIS request. */
  rescanKind?: 'additive' | 'replacement' | null
}): RefinementPolicy {
  const { mode, tier, imageCount } = input
  const result = input.result as AnyResult
  const baseReason = resolveRefinementReason(mode, result)
  // A replacement rescan that disagrees with the root outranks the base reason:
  // two answers that disagree are the more specific, more actionable story, and
  // an additive run already carries both photos so there is nothing to merge.
  const conflicting = input.rescanKind === 'replacement' &&
    hasConflictingIdentity(mode, input.priorChainResult, result)
  const reason: RefinementReason | null = conflicting ? 'conflicting_rescan' : baseReason
  const photoCap = resolvePhotoCap({ mode, tier, allowlisted: input.allowlisted })
  const slotsRemaining = Math.max(0, photoCap - Math.max(0, imageCount))

  const additiveCap = input.allowlisted
    ? Number.POSITIVE_INFINITY
    : additiveRescanCapForTier(mode, tier)
  const refinementsRemaining = Number.isFinite(additiveCap)
    ? Math.max(0, additiveCap - Math.max(0, input.priorAdditiveRescans ?? 0))
    : null

  // "Could a bigger tier take another photo here?" — only meaningful when the
  // ceiling itself has room left.
  const ceiling = MODE_PHOTO_CEILING[mode] ?? MODE_PHOTO_CEILING.identify
  const ceilingHasRoom = imageCount < ceiling
  const requiresAuth = tier === 'anonymous' && slotsRemaining === 0 && ceilingHasRoom
  const requiresUpgrade = tier === 'registered_free' &&
    ceilingHasRoom &&
    (slotsRemaining === 0 || refinementsRemaining === 0)

  const available = reason !== null && ceilingHasRoom

  return {
    available,
    reason,
    nextPhotoGuidance: available ? resolveNextPhotoGuidance(mode, result, reason) : null,
    imageCount,
    photoCap,
    slotsRemaining,
    requiresAuth,
    requiresUpgrade,
    refinementsRemaining,
  }
}
